import { Badge } from '@/components/ui/badge';
import { CheckCircle2, AlertTriangle, XCircle, Circle } from 'lucide-react';
import type { BudgetStatus } from '@/types/budget.types';

interface BudgetHealthIndicatorProps {
  status: BudgetStatus;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showText?: boolean;
  customText?: string;
}

export function BudgetHealthIndicator({
  status,
  size = 'md',
  showIcon = true,
  showText = true,
  customText,
}: BudgetHealthIndicatorProps) {
  const getIcon = () => {
    const iconSize = size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-4 w-4' : 'h-5 w-5';
    
    switch (status) {
      case 'green':
        return <CheckCircle2 className={`${iconSize} text-green-600`} />;
      case 'amber':
        return <AlertTriangle className={`${iconSize} text-amber-600`} />;
      case 'red':
        return <XCircle className={`${iconSize} text-red-600`} />;
      default:
        return <Circle className={`${iconSize} text-gray-400`} />;
    }
  };

  const getText = () => {
    if (customText) return customText;
    
    switch (status) {
      case 'green':
        return 'On Track';
      case 'amber':
        return 'Warning';
      case 'red':
        return 'Critical';
      default:
        return 'Unknown';
    }
  };

  const getVariant = () => {
    switch (status) {
      case 'green':
        return 'default' as const;
      case 'amber':
        return 'secondary' as const;
      case 'red':
        return 'destructive' as const;
      default:
        return 'outline' as const;
    }
  };

  const getTextSize = () => {
    switch (size) {
      case 'sm':
        return 'text-xs';
      case 'md':
        return 'text-sm';
      case 'lg':
        return 'text-base';
    }
  };

  if (!showIcon && !showText) {
    return null;
  }

  return (
    <Badge variant={getVariant()} className={`${getTextSize()} inline-flex items-center gap-1.5`}>
      {showIcon && getIcon()}
      {showText && <span>{getText()}</span>}
    </Badge>
  );
}

// Simple colored dot indicator
interface StatusDotProps {
  status: BudgetStatus;
  size?: 'sm' | 'md' | 'lg';
}

export function StatusDot({ status, size = 'md' }: StatusDotProps) {
  const dotSize = size === 'sm' ? 'h-2 w-2' : size === 'md' ? 'h-3 w-3' : 'h-4 w-4';
  
  const getColor = () => {
    switch (status) {
      case 'green':
        return 'bg-green-600';
      case 'amber':
        return 'bg-amber-600';
      case 'red':
        return 'bg-red-600';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <span className={`inline-block rounded-full ${dotSize} ${getColor()}`} />
  );
}
